import React, { useState } from 'react';
import { useSessionContext } from 'kmstack-shared/hooks';
import { getEnvConfig } from 'kmstack-shared/utils';

import { ChevronDownIcon, UserCircleIcon, MoonIcon, NotificationIcon, MenuIcon, ChevronLeftIcon } from '../assets/icons';
import { AuthService } from '../routes/auth.service';
import { ViewContainerAction } from '../common/detail-view/ViewContainer';
import { DashboardGlobalSearch } from './DashboardGlobalSearch';

interface DashboardTopBarProps {
  pageTitle: string;
  viewDescription?: string;
  onSidebarToggle: () => void;
  isSidebarOpen: boolean;
  isDetailViewOpen: boolean;
  onBack?: () => void;
  detailTitle?: string;
  detailActions?: ViewContainerAction[];
}

const DashboardTopBar: React.FC<DashboardTopBarProps> = ({
  pageTitle,
  viewDescription,
  onSidebarToggle,
  isSidebarOpen,
  isDetailViewOpen,
  onBack,
  detailTitle,
  detailActions
}) => {
  const { sessionContext } = useSessionContext();
  const userName = sessionContext?.user?.name || 'User';
  const accountUrl = getEnvConfig().accountUrl.replace(/\/$/, '');
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);

  const handleLogout = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setIsProfileDropdownOpen(false);
    await AuthService.logout();
  };

  return (
    <header className="h-16 bg-white shadow-sm flex items-center justify-between px-4 sm:px-6 lg:px-8 sticky top-0 z-30 relative transition-all duration-300">

      {/* Left side: Navigation or Page Title */}
      <div className="flex items-center gap-4">
        {/* Todo: for mobile, this should be improved */}
        <button
          onClick={onSidebarToggle}
          className="lg:hidden p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-md"
          aria-label={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
        >
          <MenuIcon className="w-6 h-6" />
        </button>

        {onBack ? (
          <div className="flex items-center gap-4 animate-fade-in-right">
            <button
              onClick={onBack}
              className="flex items-center gap-1 text-sm font-semibold text-gray-500 hover:text-darkGreen transition-colors group"
            >
              <div className="p-1 rounded-full group-hover:bg-gray-100 transition-colors">
                <ChevronLeftIcon className="w-5 h-5" />
              </div>
              <span>Back</span>
            </button>
            <div className="h-5 w-px bg-gray-200"></div>
            <div className="block">
              <h2 className="text-xl font-bold text-darkGreen tracking-tight">{detailTitle}</h2>
              <span className="text-xs text-gray-500">{viewDescription}</span>
            </div>
          </div>
        ) : (
          <h1 className="text-xl font-semibold text-darkGreen hidden sm:block animate-fade-in">{pageTitle}</h1>
        )}
      </div>

      {/* Right side: Actions & Profile */}
      <div className="flex items-center space-x-3 sm:space-x-5">

        {/* Detail View Actions - Injected here for seamless feel */}
        {detailActions && detailActions.length > 0 && (
          <div className="flex items-center gap-2 mr-4 border-r border-gray-200 pr-4">
            {detailActions.map((action, index) => (
              <button
                key={index}
                onClick={action.onClick}
                disabled={action.disabled}
                className={`text-sm font-semibold px-3 py-1.5 rounded-lg transition-all ${ action.variant === 'primary'
                  ? 'bg-primaryGreen text-white hover:bg-opacity-90 shadow-sm'
                  : action.variant === 'danger'
                    ? 'bg-red-50 text-red-600 hover:bg-red-100'
                    : 'text-gray-600 hover:bg-gray-100'
                  } ${ action.disabled ? 'opacity-50 cursor-not-allowed' : '' }`}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
        {/* TODO: maybe these should be hidden in detail view */}
        {/* Language Selector */}
        {!isDetailViewOpen && (
          <>
            <DashboardGlobalSearch />

            <div className="relative" >
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className="flex items-center text-sm text-gray-600 hover:text-primaryGreen"
              >
                English <ChevronDownIcon className="w-4 h-4 ml-1" />
              </button>
              {isLanguageDropdownOpen && (
                <div
                  className="absolute right-0 mt-2 w-36 bg-white rounded-md shadow-lg py-1 z-20 animate-fade-in-up text-sm"
                  onMouseLeave={() => setIsLanguageDropdownOpen(false)} // Optional: close on mouse leave
                >
                  <a href="#" onClick={(e) => { e.preventDefault(); setIsLanguageDropdownOpen(false); }} className="block px-4 py-2 text-gray-700 hover:bg-gray-100">English</a>
                  <a href="#" onClick={(e) => { e.preventDefault(); setIsLanguageDropdownOpen(false); }} className="block px-4 py-2 text-gray-700 hover:bg-gray-100">Español</a>
                  <a href="#" onClick={(e) => { e.preventDefault(); setIsLanguageDropdownOpen(false); }} className="block px-4 py-2 text-gray-700 hover:bg-gray-100">Français</a>
                </div>
              )}
            </div>

            {/* Action Icons */}
            <button className="p-2 text-gray-500 hover:text-primaryGreen hover:bg-gray-100 rounded-full" aria-label="Notifications">
              <NotificationIcon className="w-5 h-5" />
            </button>
            <button className="p-2 text-gray-500 hover:text-primaryGreen hover:bg-gray-100 rounded-full" aria-label="Toggle dark mode">
              <MoonIcon className="w-5 h-5" />
            </button>

            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                className="flex items-center"
                aria-label="User menu"
                aria-expanded={isProfileDropdownOpen}
              >
                <UserCircleIcon className="w-8 h-8 text-gray-400 rounded-full hover:ring-2 hover:ring-primaryGreen" />
              </button>
              {isProfileDropdownOpen && (
                <div
                  className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-20 animate-fade-in-up"
                  onMouseLeave={() => setIsProfileDropdownOpen(false)} // Optional: close on mouse leave
                >
                  <div className="px-4 py-2 border-b">
                    <p className="text-sm font-medium text-darkGreen">{userName}</p>
                    {/* <p className="text-xs text-gray-500">{userEmail}</p> */}
                  </div>
                  <a href={`${ accountUrl }/account/profile`} onClick={() => setIsProfileDropdownOpen(false)} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Account profile</a>
                  <a href={`${ accountUrl }/account/settings`} onClick={() => setIsProfileDropdownOpen(false)} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Account settings</a>
                  <a
                    href="#"
                    onClick={handleLogout}
                    className="block px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    Sign out
                  </a>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default DashboardTopBar;
